import React, { memo } from "react";
import { BaseEdge, useStore } from "reactflow";

function getRect(n) {
  if (!n) return null;
  const x = n.positionAbsolute?.x ?? n.position?.x;
  const y = n.positionAbsolute?.y ?? n.position?.y;
  const w = n.width ?? n.measured?.width;
  const h = n.height ?? n.measured?.height;
  if (x == null || y == null || w == null || h == null) return null;
  return {
    left: x,
    right: x + w,
    top: y,
    bottom: y + h,
    cx: x + w / 2,
  };
}

// SVG helpers
const M = (x, y) => `M ${x} ${y}`;
const L = (x, y) => `L ${x} ${y}`;
// Arc: A rx ry xAxisRotation largeArcFlag sweepFlag x y
const A = (r, x, y, sweep = 1) => `A ${r} ${r} 0 0 ${sweep} ${x} ${y}`;

/**
 * Summary dotted bracket:
 * 1) start slightly below source item at its left edge
 * 2) go DOWN
 * 3) rounded corner -> horizontal baseline
 * 4) near rules center: rounded down toward rules and immediately rounded back up (short dip)
 * 5) continue baseline
 * 6) at target right edge: rounded corner -> go UP
 * 7) end slightly below target item
 */
export const RulesBracketEdge = memo(function RulesBracketEdge(props) {
  const { id, source, target, style, data } = props;

  const nodeInternals = useStore((s) => s.nodeInternals);

  const sNode = nodeInternals.get(source);
  const tNode = nodeInternals.get(target);
  const s = getRect(sNode);
  const t = getRect(tNode);
  if (!s || !t) return null;

  const pointsTo = data?.pointsTo;

  const getAnchor = () => {
    if (!pointsTo) return null;

    // 1) "rules"
    if (typeof pointsTo === "string") {
      const n = nodeInternals.get(pointsTo);
      return getRect(n);
    }

    // 2) ["rules", "tags"]
    if (Array.isArray(pointsTo) && pointsTo.length >= 2) {
      const n1 = nodeInternals.get(pointsTo[0]);
      const n2 = nodeInternals.get(pointsTo[1]);
      const r1 = getRect(n1);
      const r2 = getRect(n2);
      if (!r1 || !r2) return null;

      return {
        top: Math.min(r1.top, r2.top),
        cx: (r1.cx + r2.cx) / 2,
      };
    }

    return null;
  };

  const anchor = getAnchor();
  if (!anchor) return null;

  // ========= Tunables =========
  const RADIUS = 14;

  // 1) start/end
  const START_BELOW_SOURCE = 10; // px below source bottom
  const END_BELOW_TARGET = 10;   // px below target bottom

  // 2) how deep left leg goes down before turning into baseline
  const LEFT_DROP = 28;

  // 4) dip near rules (toward rules)
  const BASELINE_ABOVE_RULES = 34; // baseline is above rules top
  // const DIP_ABOVE_RULES = 10;      // dip track is closer to rules (still above)
  // const DIP_HALF_WIDTH = 18;       // half width of the dip "notch"
  // const DIP_DEPTH_LIMIT = 40;      // safety limit (optional)
  // ==============================================

  // X span (exactly from left edge of source to right edge of target)
  const xStart = s.left;
  const xEnd = t.right;

  // 1) Start slightly below source
  const yStart = s.bottom + START_BELOW_SOURCE;

  // 7) End slightly below target
  const yEnd = t.bottom + END_BELOW_TARGET;

  // Baseline: must be BELOW yStart (so left leg truly goes down),
  // and ABOVE rules (as per your “summary bracket under the flow but over rules area”).
  const baselineCandidate = anchor.top - BASELINE_ABOVE_RULES;

  // Ensure baseline is at least below start by LEFT_DROP (so пункт 2: "йде вниз")
  const baselineY = Math.max(baselineCandidate, yStart + LEFT_DROP);

  // Dip track: closer to rules than baseline (so it dips "к ньому" = toward rules)
  // let dipY = anchor.top - DIP_ABOVE_RULES;

  // Ensure dip is not ABOVE baseline (must go downward for пункт 4)
  // dipY = Math.max(dipY, baselineY + RADIUS * 2);

  // Optional safety to avoid absurd depth
  // dipY = Math.min(dipY, baselineY + DIP_DEPTH_LIMIT);

  // Dip X center: "до центра центрального айтема" (rules center)
  // const dipCenterX = anchor.cx;

  // const dipLeftX = dipCenterX - DIP_HALF_WIDTH;
  // const dipRightX = dipCenterX + DIP_HALF_WIDTH;

  // Clamp dip inside span, leaving room for arcs
  // const minX = xStart + RADIUS * 3;
  // const maxX = xEnd - RADIUS * 3;
  // const dl = Math.max(dipLeftX, minX);
  // const dr = Math.min(dipRightX, maxX);

  // Left and right corner x positions
  const xL = xStart;
  const xR = xEnd;

  // Build path
  const d = [];

  // (1) Start
  d.push(M(xL, yStart));

  // (2) Go down towards baseline, stop R before baseline to make rounded corner
  d.push(L(xL, baselineY - RADIUS));

  // (3) Rounded corner from vertical-down to horizontal-right on baseline
  // from (xL, baselineY-R) to (xL+R, baselineY)
  d.push(A(RADIUS, xL + RADIUS, baselineY, 0));

  // (4) Baseline to chevron start (near rules center)
  const notchCenterX = anchor.cx;

  const NOTCH_HALF_W = 18;
  const notchLeftX = Math.max(notchCenterX - NOTCH_HALF_W, xStart + RADIUS * 3);
  const notchRightX = Math.min(notchCenterX + NOTCH_HALF_W, xEnd - RADIUS * 3);

  const NOTCH_GAP_ABOVE_RULES = 2;
  const notchTipY = anchor.top - NOTCH_GAP_ABOVE_RULES;

  d.push(L(notchLeftX, baselineY));

  d.push(L(notchCenterX, notchTipY));
  d.push(L(notchRightX, baselineY));

  // (5) Continue baseline to right edge, stop R before right to make rounded corner up
  d.push(L(xR - RADIUS, baselineY));

  // (6) Rounded corner from horizontal-right to vertical-up
  // from (xR-R, baselineY) to (xR, baselineY-R)
  d.push(A(RADIUS, xR, baselineY - RADIUS, 0));

  // (7) Go up and end slightly below target item
  d.push(L(xR, yEnd));

  return (
    <BaseEdge
      id={id}
      path={d.join(" ")}
      style={{
        ...style,
        strokeWidth: 1,
        strokeDasharray: "2 2",
        strokeLinecap: "round",
        strokeLinejoin: "round",
      }}
    />
  );
});